/**
 * Shared pricing computations used by CartPage and PaymentPage.
 *
 * Requires the consuming component to provide (from cartMixin):
 *   - this.not_draft_products
 *   - this.non_draft_forms
 *   - this.getDesignServiceTotalFee
 *   - this.getTotalPrice
 *   - this.getProducts()
 *   - this.getVolumeDiscountPerCupSize()
 *   - this.billingCharge (data – defaults to false)
 *   - voucher accessors (see _pricingVoucher / _pricingIgnoreAdditional25Fee)
 */

const DEFAULT_VAT_RATE = 25.5

export default {
  computed: {
    transportCost() {
      return this.transportCostParts.reduce((sum, part) => sum + part, 0)
    },

    /**
     * Transport cost split the way the backend charges it (models.pre_save_Order):
     * webstore products and cups are separate charges, and VAT is rounded on
     * each one. Rounding the sum instead can be a cent off (e.g. 12.5 + 12.5).
     */
    transportCostParts() {
      const fv = typeof this.getFixedVoucher === 'function'
        ? this.getFixedVoucher()
        : this.$store?.getters?.getFixedVoucher
      if (fv && fv.fixed_transport_cost != null) {
        return [fv.fixed_transport_cost]
      }

      let storeProductUnits = 0
      let cupProductPieces = 0
      for (let i = 0; i < this.not_draft_products.length; i++) {
        const rtype = this.$store.getters.getProductById(this.not_draft_products[i].product).resource_type
        if (rtype === 'Cup') {
          cupProductPieces += this.not_draft_products[i].amount
        } else if (rtype !== 'CandyBox') {
          storeProductUnits += this.not_draft_products[i].amount
        }
      }

      // Webstore (non-Cup, non-CandyBox) transport tiers
      let storeCost = 0
      if (storeProductUnits == 1) storeCost = 12.5
      else if (storeProductUnits == 2) storeCost = 18
      else if (storeProductUnits == 3) storeCost = 28
      else if (storeProductUnits == 4) storeCost = 39
      else if (storeProductUnits >= 5) storeCost = 47

      // Cup transport tiers (by total pieces)
      let cupCost = 0
      if (cupProductPieces > 0 && cupProductPieces < 1000) cupCost = 12.5
      else if (cupProductPieces >= 1000 && cupProductPieces < 5000) cupCost = 20
      else if (cupProductPieces >= 5000) cupCost = 40

      return [storeCost, cupCost]
    },

    additionalChargeCost() {
      const allCandyBox = this.not_draft_products.length > 0 && this.not_draft_products.every(item =>
        this.$store.getters.getProductById(item.product).resource_type === 'CandyBox'
      )
      if (allCandyBox) return 0
      let total = this.getDesignServiceTotalFee + this.getTotalPrice
      const ignoreAdditionalFee = this._pricingIgnoreAdditional25Fee
      if (total < 150 && !ignoreAdditionalFee) {
        return 25
      }
      return 0
    },

    /**
     * Computes the VAT amount per product line, grouped by VAT rate.
     * Fees (transport, additional charge, billing, design service) use the default 25.5%.
     *
     * Returns: [ { rate: 25.5, noVat: 120.00, withVat: 150.60 }, { rate: 14, noVat: 50.00, withVat: 57.00 } ]
     */
    vatBreakdown() {
      const buckets = {}

      const addToBucket = (rate, amount) => {
        if (amount <= 0) return
        if (!buckets[rate]) buckets[rate] = { rate, noVat: 0, withVat: 0 }
        const rounded = Math.round(amount * 100) / 100
        buckets[rate].noVat += rounded
        buckets[rate].withVat += Math.round(rounded * (1 + rate / 100) * 100) / 100
      }

      // Product lines — each product has its own vat_rate
      this.getProducts().filter(p => p.resource_type != 'Plate').forEach(product => {
        const qty = this.$store.getters.getItemsTotalAmountByProduct(product.id)
        if (qty > 0) {
          const priceEach = this.getVolumeDiscountPerCupSize(product.id)
          const vatRate = product.vat_rate != null ? product.vat_rate : DEFAULT_VAT_RATE
          addToBucket(vatRate, qty * priceEach)
        }
      })

      // Plate products with materials
      this.getProducts().filter(p => p.resource_type == 'Plate').forEach(product => {
        const materials = new Set()
        this.not_draft_products.filter(item => item.product == product.id).forEach(item => {
          materials.add(item.material)
        })
        Array.from(materials).forEach(material => {
          const qty = this.$store.getters.getItemsTotalAmountByProduct(product.id, material)
          const priceEach = this.getVolumeDiscountPerCupSize(product.id, material)
          const vatRate = product.vat_rate != null ? product.vat_rate : DEFAULT_VAT_RATE
          addToBucket(vatRate, qty * priceEach)
        })
      })

      // Shipping voucher price
      if (this.getShipping) {
        addToBucket(DEFAULT_VAT_RATE, this.getShipping.price / 100)
      }

      // Fees — always at default VAT rate
      addToBucket(DEFAULT_VAT_RATE, this.getDesignServiceTotalFee)
      addToBucket(DEFAULT_VAT_RATE, this.additionalChargeCost)
      this.transportCostParts.forEach(part => addToBucket(DEFAULT_VAT_RATE, part))

      if (this.billingCharge) {
        addToBucket(DEFAULT_VAT_RATE, 30)
      }

      return Object.values(buckets).sort((a, b) => b.rate - a.rate)
    },

    finalPriceNoVat() {
      let total = this.vatBreakdown.reduce((sum, b) => sum + b.noVat, 0)

      // apply discount
      const voucher = this._pricingVoucher
      if (voucher.amountOff != null) {
        total -= parseFloat(voucher.amountOff) / 100
      } else if (voucher.percentOff != null) {
        total = total * (1 - parseFloat(voucher.percentOff) / 100)
      }

      return total.toFixed(2)
    },

    finalPrice() {
      let total = this.vatBreakdown.reduce((sum, b) => sum + b.withVat, 0)

      // apply discount
      const voucher = this._pricingVoucher
      if (voucher.amountOff != null) {
        total -= parseFloat(voucher.amountOff) / 100
      } else if (voucher.percentOff != null) {
        total = total * (1 - parseFloat(voucher.percentOff) / 100)
      }

      return total.toFixed(2)
    },

    /** Total VAT amount (finalPrice - finalPriceNoVat) */
    vatAmount() {
      return (parseFloat(this.finalPrice) - parseFloat(this.finalPriceNoVat)).toFixed(2)
    },

    /** Whether there are multiple VAT rates in the current order */
    hasMultipleVatRates() {
      return this.vatBreakdown.length > 1
    },

    /**
     * Returns a normalised voucher object. Each page stores voucher data differently:
     * - CartPage uses getVoucher() store getter
     * - PaymentPage uses local data (voucherAmountOff, voucherPercentOff)
     */
    _pricingVoucher() {
      if (typeof this.getVoucher === 'function') {
        const v = this.getVoucher()
        return {
          amountOff: v.voucherAmountOff ?? null,
          percentOff: v.voucherPercentOff ?? null,
        }
      }
      return {
        amountOff: this.voucherAmountOff ?? null,
        percentOff: this.voucherPercentOff ?? null,
      }
    },

    _pricingIgnoreAdditional25Fee() {
      if (typeof this.getFixedVoucher === 'function') {
        const fv = this.getFixedVoucher()
        return fv?.ignore_additional_25_fee || false
      }
      return this.voucherIgnoreAdditional25Fee || false
    },
  }
}
